// Workforce › Templates › Overlay (TEMPLATES-T3, design §1 "Client overlay"): the company's own
// layer over every role (template_key null), per-role overlays, and the vertical packs — the
// published ones to apply, and the applied ones (scope vertical) to edit or archive. Overlays only
// tighten: access caps are ceilings and blocked actions become Never. Edits are modals; an update
// or archive sends the row's version (If-Match), and a 409 says so inline and reloads.
import { ApiError, newIdempotencyKey } from "@companyos/sdk";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  NativeSelect,
  NativeSelectOption,
  Notice,
  Textarea,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArchiveIcon, PackagePlusIcon, PencilIcon, PlusIcon } from "lucide-react";
import { type FormEvent, type ReactNode, useId, useState } from "react";
import type { Api } from "../../api/client";
import { useCurrency } from "../agents/agent-parts";
import type { CompanyAccess } from "../company";
import { ConfirmRowAction } from "../confirm-row-action";
import { Facts, LoadError, Loading, Nothing, Section } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { RowAction, RowActions } from "../settings/row-actions";
import {
  ACCESS_FAMILIES,
  type AccessFamily,
  type AccessLevel,
  FAMILY_LABELS,
  formatMinor,
  humanKey,
  LEVEL_LABELS,
  LEVELS,
  type ModelTier,
  majorOf,
  minorOf,
  overlaysQuery,
  parseList,
  type RoleCard,
  type TemplateOverlay,
  TIER_LABELS,
  templateKeys,
  type VerticalPack,
} from "./role-model";

type Editing = { kind: "new-company" } | { kind: "new-role" } | { kind: "edit"; id: string };

const conflictText = "Changed by someone else since you opened it. Reloaded: check and try again.";

export function OverlayTab({
  api,
  company,
  roles,
  packs,
}: {
  api: Api;
  company: CompanyAccess;
  roles: RoleCard[];
  packs: VerticalPack[];
}) {
  const co = company.id;
  const qc = useQueryClient();
  const overlays = useQuery(overlaysQuery(api, co));
  const currency = useCurrency(api, co) ?? "USD";
  const [editing, setEditing] = useState<Editing | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const changed = () => qc.invalidateQueries({ queryKey: templateKeys.all(co) });

  const archive = useMutation({
    mutationFn: (o: TemplateOverlay) =>
      api.client.templateOverlays.archive({ params: { co, id: o.id }, ifMatch: o.version }),
    onSuccess: (o) => {
      setRowError(null);
      toast.add({ title: `${o.name} archived`, type: "success" });
    },
    onError: (e) =>
      setRowError(
        e instanceof ApiError && e.status === 409 ? conflictText : problemText(e, "Not archived."),
      ),
    onSettled: changed,
  });
  const [applyKey, setApplyKey] = useState(newIdempotencyKey);
  const apply = useMutation({
    mutationFn: (p: VerticalPack) =>
      api.client.verticalPacks.apply({ params: { co, id: p.id }, idempotencyKey: applyKey }),
    onSuccess: (r, p) => {
      setRowError(null);
      setApplyKey(newIdempotencyKey());
      toast.add({
        title: `${p.name} applied to ${r.data.length} role${r.data.length === 1 ? "" : "s"}`,
        type: "success",
      });
    },
    onError: (e) => {
      setApplyKey(newIdempotencyKey());
      setRowError(problemText(e, "The pack was not applied."));
    },
    onSettled: changed,
  });

  if (overlays.isPending) return <Loading label="Loading overlays" rows={3} />;
  if (overlays.isError) return <LoadError what="Overlays" error={overlays.error} />;
  const live = overlays.data.data.filter((o) => o.archived_at === null);
  const companyOverlay = live.find((o) => o.scope === "company" && o.template_key === null);
  const roleOverlays = live.filter((o) => o.scope === "company" && o.template_key !== null);
  const applied = live.filter((o) => o.scope === "vertical");
  const published = packs.filter((p) => p.status === "published");
  const roleName = (key: string | null) =>
    roles.find((r) => r.template.key === key)?.template.name ?? key ?? "Every role";
  const editingOverlay =
    editing?.kind === "edit" ? live.find((o) => o.id === editing.id) : undefined;

  const rowActions = (o: TemplateOverlay) => (
    <RowActions>
      <RowAction
        icon={PencilIcon}
        label={`Edit ${o.name}`}
        onClick={() => setEditing({ kind: "edit", id: o.id })}
      />
      <ConfirmRowAction
        icon={ArchiveIcon}
        label={`Archive ${o.name}`}
        title={`Archive ${o.name}?`}
        description="DEs drop it at their next deploy."
        confirmLabel="Archive"
        onConfirm={() => archive.mutate(o)}
      />
    </RowActions>
  );

  return (
    <div className="flex min-w-0 flex-col" data-slot="overlay-tab">
      {rowError ? (
        <Notice tone="danger" role="alert" data-slot="overlay-row-error">
          {rowError}
        </Notice>
      ) : null}
      <Section
        id="overlay-company"
        title="Company overlay"
        actions={
          companyOverlay ? (
            <RowAction
              icon={PencilIcon}
              label="Edit the company overlay"
              onClick={() => setEditing({ kind: "edit", id: companyOverlay.id })}
            />
          ) : (
            <Button size="sm" onClick={() => setEditing({ kind: "new-company" })}>
              <PlusIcon aria-hidden="true" />
              Add overlay
            </Button>
          )
        }
      >
        {companyOverlay ? (
          <div data-slot="company-overlay">
            <OverlayFacts overlay={companyOverlay} currency={currency} />
          </div>
        ) : (
          <Nothing title="No company overlay" />
        )}
      </Section>

      <Section
        id="overlay-roles"
        title="Role overlays"
        actions={
          roles.length ? (
            <RowAction
              icon={PlusIcon}
              label="Add a role overlay"
              onClick={() => setEditing({ kind: "new-role" })}
            />
          ) : null
        }
      >
        {roleOverlays.length ? (
          <OverlayList
            rows={roleOverlays}
            sub={(o) => roleName(o.template_key)}
            actions={rowActions}
            slot="role-overlays"
          />
        ) : (
          <p className="text-sm text-muted-foreground">None</p>
        )}
      </Section>

      <Section id="overlay-packs" title="Vertical packs">
        {published.length ? (
          <ul
            className="flex flex-col divide-y divide-border rounded-lg border border-border"
            data-slot="vertical-packs"
          >
            {published.map((p) => (
              <li
                key={p.id}
                data-pack={p.key}
                className="group/row flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2"
              >
                <span className="text-sm font-medium">{p.name}</span>
                {p.description ? (
                  <InfoTip label={`About ${p.name}`}>{p.description}</InfoTip>
                ) : null}
                <span className="flex flex-wrap gap-1">
                  {p.base_template_keys.map((k) => (
                    <Badge key={k} variant="outline">
                      {roleName(k)}
                    </Badge>
                  ))}
                </span>
                <span className="ml-auto">
                  <RowActions>
                    <ConfirmRowAction
                      icon={PackagePlusIcon}
                      label={`Apply ${p.name}`}
                      title={`Apply ${p.name}?`}
                      description="Adds one overlay per role it covers; edit them afterwards."
                      confirmLabel="Apply"
                      destructive={false}
                      disabled={apply.isPending}
                      onConfirm={() => apply.mutate(p)}
                    />
                  </RowActions>
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No packs published</p>
        )}
      </Section>

      <Section id="overlay-applied" title="Applied packs">
        {applied.length ? (
          <OverlayList
            rows={applied}
            sub={(o) => roleName(o.template_key)}
            actions={rowActions}
            slot="applied-packs"
          />
        ) : (
          <p className="text-sm text-muted-foreground">None</p>
        )}
      </Section>

      {editing && (editing.kind !== "edit" || editingOverlay) ? (
        <OverlayDialog
          api={api}
          co={co}
          currency={currency}
          roles={roles}
          overlay={editingOverlay}
          forRole={editing.kind === "new-role"}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}

function OverlayList({
  rows,
  sub,
  actions,
  slot,
}: {
  rows: TemplateOverlay[];
  sub: (o: TemplateOverlay) => ReactNode;
  actions: (o: TemplateOverlay) => ReactNode;
  slot: string;
}) {
  return (
    <ul
      className="flex flex-col divide-y divide-border rounded-lg border border-border"
      data-slot={slot}
    >
      {rows.map((o) => (
        <li
          key={o.id}
          data-overlay={o.id}
          className="group/row flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2"
        >
          <span className="text-sm font-medium">{o.name}</span>
          <span className="text-xs text-muted-foreground">{sub(o)}</span>
          <span className="text-xs text-muted-foreground">{overlayLine(o)}</span>
          <span className="ml-auto">{actions(o)}</span>
        </li>
      ))}
    </ul>
  );
}

/** "2 caps · 1 blocked · 3 skills" — what an overlay changes, in a few words. */
export function overlayLine(o: TemplateOverlay): string {
  const parts = [
    Object.keys(o.access_caps).length ? `${Object.keys(o.access_caps).length} caps` : "",
    o.blocked_actions.length ? `${o.blocked_actions.length} blocked` : "",
    o.add_skill_keys.length ? `+${o.add_skill_keys.length} skills` : "",
    o.block_skill_keys.length ? `−${o.block_skill_keys.length} skills` : "",
    Object.keys(o.kpi_overrides).length ? `${Object.keys(o.kpi_overrides).length} targets` : "",
    o.model_tier ? TIER_LABELS[o.model_tier] : "",
  ].filter(Boolean);
  return parts.join(" · ");
}

function OverlayFacts({ overlay: o, currency }: { overlay: TemplateOverlay; currency: string }) {
  const caps = Object.entries(o.access_caps) as [AccessFamily, AccessLevel][];
  const kpis = Object.entries(o.kpi_overrides);
  return (
    <Facts
      items={[
        { label: "Name", value: o.name },
        {
          label: "How we work",
          value: o.context_text ? (
            <span className="line-clamp-4 whitespace-pre-wrap">{o.context_text}</span>
          ) : (
            "—"
          ),
        },
        { label: "Tone", value: o.tone_text || "—" },
        { label: "Extra skills", value: o.add_skill_keys.join(", ") || "—" },
        { label: "Blocked skills", value: o.block_skill_keys.join(", ") || "—" },
        {
          label: "Access caps",
          value: caps.length
            ? caps.map(([f, l]) => `${FAMILY_LABELS[f]}: ${LEVEL_LABELS[l]}`).join(", ")
            : "—",
        },
        { label: "Blocked actions", value: o.blocked_actions.join(", ") || "—" },
        {
          label: "KPI targets",
          value: kpis.length
            ? kpis.map(([k, t]) => `${humanKey(k)}: ${t ?? "none"}`).join(", ")
            : "—",
        },
        { label: "Model tier", value: o.model_tier ? TIER_LABELS[o.model_tier] : "The role's" },
        {
          label: "Budget",
          value: o.budget_minor === null ? "The role's" : formatMinor(o.budget_minor, currency),
        },
      ]}
    />
  );
}

type OverlayForm = {
  templateKey: string;
  name: string;
  context: string;
  tone: string;
  addSkills: string;
  blockSkills: string;
  caps: Partial<Record<AccessFamily, AccessLevel>>;
  blocked: string;
  kpis: Record<string, string>;
  tier: ModelTier | "";
  budget: string;
};

const formOf = (o: TemplateOverlay | undefined): OverlayForm => ({
  templateKey: o?.template_key ?? "",
  name: o?.name ?? "",
  context: o?.context_text ?? "",
  tone: o?.tone_text ?? "",
  addSkills: o?.add_skill_keys.join(", ") ?? "",
  blockSkills: o?.block_skill_keys.join(", ") ?? "",
  caps: { ...(o?.access_caps ?? {}) },
  blocked: o?.blocked_actions.join(", ") ?? "",
  kpis: Object.fromEntries(
    Object.entries(o?.kpi_overrides ?? {}).map(([k, v]) => [k, v === null ? "" : String(v)]),
  ),
  tier: o?.model_tier ?? "",
  budget: o?.budget_minor == null ? "" : majorOf(o.budget_minor),
});

const numberOk = (t: string) => t.trim() === "" || /^\d{1,10}(\.\d{1,4})?$/.test(t.trim());

/** The overlay's fields as the API takes them (blank text: null; blank target: left out). */
export function overlayBody(f: OverlayForm) {
  const kpi_overrides: Record<string, number> = {};
  for (const [k, v] of Object.entries(f.kpis))
    if (v.trim()) kpi_overrides[k] = Number.parseFloat(v.trim());
  return {
    name: f.name.trim(),
    context_text: f.context.trim() || null,
    tone_text: f.tone.trim() || null,
    add_skill_keys: parseList(f.addSkills),
    block_skill_keys: parseList(f.blockSkills),
    access_caps: f.caps,
    blocked_actions: parseList(f.blocked),
    kpi_overrides,
    model_tier: f.tier || null,
    budget_minor: f.budget.trim() ? minorOf(f.budget) : null,
  };
}

export function OverlayDialog({
  api,
  co,
  currency,
  roles,
  overlay,
  forRole,
  onClose,
}: {
  api: Api;
  co: string;
  currency: string;
  roles: RoleCard[];
  /** The overlay edited (absent: a new one). */
  overlay: TemplateOverlay | undefined;
  /** A new overlay for one role (it asks which). */
  forRole: boolean;
  onClose: () => void;
}) {
  const id = useId();
  const qc = useQueryClient();
  const [form, setForm] = useState<OverlayForm>(() => formOf(overlay));
  const [idem, setIdem] = useState(newIdempotencyKey);
  const [error, setError] = useState<string | null>(null);
  const set = (patch: Partial<OverlayForm>) => setForm((f) => ({ ...f, ...patch }));
  const roleKey = overlay ? overlay.template_key : forRole ? form.templateKey : null;
  // The KPI keys the overlay may retarget: its role's, or every role's for the company overlay.
  const kpiKeys = [
    ...new Set([
      ...roles
        .filter((r) => roleKey === null || r.template.key === roleKey)
        .flatMap((r) => (r.version.kpis ?? []).map((k) => k.key)),
      ...Object.keys(form.kpis),
    ]),
  ];
  const save = useMutation({
    mutationFn: () => {
      const body = overlayBody(form);
      return overlay
        ? api.client.templateOverlays.update({
            params: { co, id: overlay.id },
            ifMatch: overlay.version,
            body,
          })
        : api.client.templateOverlays.create({
            params: { co },
            idempotencyKey: idem,
            body: { ...body, template_key: forRole ? form.templateKey : null },
          });
    },
    onSuccess: async (o) => {
      await qc.invalidateQueries({ queryKey: templateKeys.all(co) });
      toast.add({ title: `${o.name} saved`, type: "success" });
      onClose();
    },
    onError: (e) => {
      if (!overlay) setIdem(newIdempotencyKey());
      if (e instanceof ApiError && e.status === 409) {
        setError(conflictText);
        void qc.invalidateQueries({ queryKey: templateKeys.overlays(co) });
      } else setError(problemText(e, "Not saved."));
    },
  });
  const valid =
    Boolean(form.name.trim()) &&
    (!forRole || overlay !== undefined || Boolean(form.templateKey)) &&
    Object.values(form.kpis).every(numberOk) &&
    (form.budget.trim() === "" || minorOf(form.budget) !== null);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (valid) save.mutate();
  };
  const title = overlay
    ? `Edit ${overlay.name}`
    : forRole
      ? "Add a role overlay"
      : "Add the company overlay";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[640px]">
        <form onSubmit={submit} className="flex flex-col gap-4" data-slot="overlay-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-1">
              {title}
              <InfoTip label="About overlays">
                Your layer over the role: it can tighten access and block actions, never widen them.
              </InfoTip>
            </DialogTitle>
          </DialogHeader>
          {forRole && !overlay ? (
            <Field>
              <FieldLabel htmlFor={`${id}-role`}>Role</FieldLabel>
              <NativeSelect
                id={`${id}-role`}
                value={form.templateKey}
                onChange={(e) => set({ templateKey: e.target.value })}
              >
                <NativeSelectOption value="">Choose a role…</NativeSelectOption>
                {roles.map((r) => (
                  <NativeSelectOption key={r.template.id} value={r.template.key}>
                    {r.template.name}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
          ) : null}
          <Field>
            <FieldLabel htmlFor={`${id}-name`}>Name</FieldLabel>
            <Input
              id={`${id}-name`}
              required
              maxLength={120}
              value={form.name}
              onChange={(e) => set({ name: e.target.value })}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor={`${id}-context`}>How we work</FieldLabel>
            <Textarea
              id={`${id}-context`}
              rows={4}
              maxLength={8000}
              value={form.context}
              onChange={(e) => set({ context: e.target.value })}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor={`${id}-tone`}>Tone</FieldLabel>
            <Textarea
              id={`${id}-tone`}
              rows={2}
              maxLength={2000}
              value={form.tone}
              onChange={(e) => set({ tone: e.target.value })}
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field>
              <FieldLabel htmlFor={`${id}-add`}>Extra skills</FieldLabel>
              <Input
                id={`${id}-add`}
                value={form.addSkills}
                placeholder="skill-key, …"
                onChange={(e) => set({ addSkills: e.target.value })}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor={`${id}-block`}>Blocked skills</FieldLabel>
              <Input
                id={`${id}-block`}
                value={form.blockSkills}
                placeholder="skill-key, …"
                onChange={(e) => set({ blockSkills: e.target.value })}
              />
            </Field>
          </div>
          <fieldset className="flex flex-col gap-2" data-slot="overlay-caps">
            <legend className="mb-1 flex items-center gap-1 text-sm font-medium">
              Access caps
              <InfoTip label="About access caps">
                The most a DE may get per family. None closes it.
              </InfoTip>
            </legend>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {ACCESS_FAMILIES.map((f) => (
                <div key={f} className="flex items-center justify-between gap-2 text-sm">
                  <span aria-hidden="true">{FAMILY_LABELS[f]}</span>
                  <NativeSelect
                    aria-label={`${FAMILY_LABELS[f]} cap`}
                    className="h-8 w-32"
                    value={form.caps[f] ?? ""}
                    onChange={(e) => {
                      const { [f]: _drop, ...rest } = form.caps;
                      set({
                        caps: e.target.value
                          ? { ...rest, [f]: e.target.value as AccessLevel }
                          : rest,
                      });
                    }}
                  >
                    <NativeSelectOption value="">No cap</NativeSelectOption>
                    {LEVELS.map((l) => (
                      <NativeSelectOption key={l} value={l}>
                        {LEVEL_LABELS[l]}
                      </NativeSelectOption>
                    ))}
                  </NativeSelect>
                </div>
              ))}
            </div>
          </fieldset>
          <Field>
            <FieldLabel htmlFor={`${id}-blocked`} className="flex items-center gap-1">
              Blocked actions
              <InfoTip label="About blocked actions">
                Action classes no DE may do; they become Never.
              </InfoTip>
            </FieldLabel>
            <Input
              id={`${id}-blocked`}
              value={form.blocked}
              placeholder="external, bulk, …"
              onChange={(e) => set({ blocked: e.target.value })}
            />
          </Field>
          {kpiKeys.length ? (
            <fieldset className="flex flex-col gap-2" data-slot="overlay-kpis">
              <legend className="mb-1 text-sm font-medium">KPI targets</legend>
              <div className="grid gap-2 sm:grid-cols-2">
                {kpiKeys.map((k) => (
                  <div key={k} className="flex items-center justify-between gap-2 text-sm">
                    <span className="truncate" aria-hidden="true">
                      {humanKey(k)}
                    </span>
                    <Input
                      aria-label={`${humanKey(k)} target`}
                      inputMode="decimal"
                      className="h-8 w-28"
                      placeholder="Role's"
                      aria-invalid={!numberOk(form.kpis[k] ?? "") || undefined}
                      value={form.kpis[k] ?? ""}
                      onChange={(e) => set({ kpis: { ...form.kpis, [k]: e.target.value } })}
                    />
                  </div>
                ))}
              </div>
            </fieldset>
          ) : null}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field>
              <FieldLabel htmlFor={`${id}-tier`}>Model tier</FieldLabel>
              <NativeSelect
                id={`${id}-tier`}
                value={form.tier}
                onChange={(e) => set({ tier: e.target.value as ModelTier | "" })}
              >
                <NativeSelectOption value="">The role's</NativeSelectOption>
                {(Object.keys(TIER_LABELS) as ModelTier[]).map((t) => (
                  <NativeSelectOption key={t} value={t}>
                    {TIER_LABELS[t]}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
            <Field>
              <FieldLabel htmlFor={`${id}-budget`}>Monthly budget ({currency})</FieldLabel>
              <Input
                id={`${id}-budget`}
                inputMode="decimal"
                placeholder="The role's"
                aria-invalid={
                  (form.budget.trim() !== "" && minorOf(form.budget) === null) || undefined
                }
                value={form.budget}
                onChange={(e) => set({ budget: e.target.value })}
              />
            </Field>
          </div>
          {error ? (
            <Notice tone="danger" role="alert" data-slot="overlay-error">
              {error}
            </Notice>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!valid || save.isPending}>
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
